import {
    Box,
    GridItem,
    Text,
} from '@chakra-ui/react';
import {
    ServiceIconButton,
    DateInputWithPickerField,
    PopoverTag,
    BasicSelect,
} from '@acp/core-ui';
import {
    GAS_APPLIANCE_VALUES,
    IntegratedPropertyService,
    ServiceProviderOption,
    ServiceShortName,
} from '@acp/shared';
import { Controller, useFormContext } from 'react-hook-form';
import {
    memo,
    useMemo,
    useEffect,
} from 'react';
import {
    parseISO,
    isValid as isValidDate,
    format,
    startOfDay,
    isAfter,
} from 'date-fns';
import {
    ServiceGrid,
} from './common';
import { getSuggestedNextServiceDate } from './helpers';
import { ServiceProviderSelect } from './service-provider-select';
import { FileInputWithCounter } from '../file-icon-with-counter';

// Constants / configuration
const SUGGESTED_DATE_LOCALE = 'en-AU';
const SUGGESTED_LABEL = 'Suggested date';
const COLOR_SCHEME_INFO = 'info';

interface SuggestedDatePopoverProps {
    suggested: Date;
    frequencyMonths: number;
}

const SuggestedDatePopover = memo(({ suggested, frequencyMonths }: SuggestedDatePopoverProps) => {
    const pluralised = `${frequencyMonths} month${frequencyMonths === 1 ? '' : 's'} frequency`;
    return (
        <Box mt={1} display="inline-block">
            <PopoverTag
                popoverPlacement="top"
                colorScheme={COLOR_SCHEME_INFO}
                title={`${SUGGESTED_LABEL}: ${suggested.toLocaleDateString(SUGGESTED_DATE_LOCALE)}`}
            >
                <Box fontSize="sm">
                    <Text fontWeight="bold">{SUGGESTED_LABEL}</Text>
                    <Text mb={2}>
                        Based on
                        {' '}
                        {pluralised}
                    </Text>
                </Box>
            </PopoverTag>
        </Box>
    );
});
SuggestedDatePopover.displayName = 'SuggestedDatePopover';

export function EditCompliance({
    portfolioPropertyId,
    serviceIndex,
    service,
    providerOptions,
    isLoadingProviderOptions,
}: {
    portfolioPropertyId: number;
    serviceIndex: number;
    service: IntegratedPropertyService;
    providerOptions: ServiceProviderOption[];
    isLoadingProviderOptions: boolean;
}) {
    const form = useFormContext();

    // Build field names once
    const FIELD_NAMES = useMemo(() => {
        const base = `properties.${portfolioPropertyId}.services.${serviceIndex}`;
        return {
            lastServiceDate: `${base}.lastServiceDate`,
            nextServiceDate: `${base}.nextServiceDate`,
            isFurtherWorksRequired: `${base}.isFurtherWorksRequired`,
            providedBy: `${base}.providedBy`,
            gasApplianceCountValue: `properties.${portfolioPropertyId}.gasApplianceCountValue`,
        } as const;
    }, [portfolioPropertyId, serviceIndex]);

    // Watch raw last service date (could be Date | string | null)
    const lastRaw = form.watch(FIELD_NAMES.lastServiceDate);
    const lastDate: Date | null = useMemo(() => {
        if (lastRaw instanceof Date) return lastRaw;
        if (typeof lastRaw === 'string' && lastRaw.trim() !== '') {
            const parsed = parseISO(lastRaw);
            return isValidDate(parsed) ? parsed : null;
        }
        return null;
    }, [lastRaw]);
    const frequencyMonths = service.serviceFrequencyMonths ?? 0;

    const { isDirty: lastIsDirty } = form.getFieldState(
        FIELD_NAMES.lastServiceDate,
        form.formState
    );
    useEffect(() => {
        if (!lastIsDirty) return;

        if (!lastDate || frequencyMonths <= 0) {
            return;
        }

        const today = startOfDay(new Date());
        if (isAfter(startOfDay(lastDate), today)) return;

        const suggestedDate = getSuggestedNextServiceDate(lastDate, frequencyMonths);
        if (!suggestedDate) return;

        const currentNext = form.getValues(FIELD_NAMES.nextServiceDate);
        if (suggestedDate === currentNext) return;

        form.setValue(
            FIELD_NAMES.nextServiceDate,
            format(suggestedDate, 'yyyy-MM-dd'),
            { shouldDirty: true, shouldValidate: true }
        );
    }, [lastDate, frequencyMonths]);

    const gasApplianceCountValue = form.watch(FIELD_NAMES.gasApplianceCountValue);
    const isGasService = service.shortName === ServiceShortName.Gas;

    const { isDirty: gasApplianceCountValueIsDirty } = form.getFieldState(
        FIELD_NAMES.gasApplianceCountValue,
        form.formState
    );

    useEffect(() => {
        if (!gasApplianceCountValueIsDirty) return;

        const clearingFieldValues = [null, undefined, GAS_APPLIANCE_VALUES.NONE];

        if (isGasService && clearingFieldValues.includes(gasApplianceCountValue)) {
            const fieldsToBeCleared = [
                FIELD_NAMES.nextServiceDate,
                FIELD_NAMES.lastServiceDate,
            ];

            fieldsToBeCleared.forEach((field) => {
                form.setValue(field, null, { shouldDirty: true, shouldValidate: true });
            });

            form.setValue(
                FIELD_NAMES.providedBy,
                { id: null, providedBy: null },
                { shouldDirty: true, shouldValidate: true }
            );
        }
    }, [gasApplianceCountValue]);

    const suggested = useMemo(
        () => getSuggestedNextServiceDate(lastDate, frequencyMonths),
        [lastDate, frequencyMonths]
    );

    const { error: lastDateError } = form.getFieldState(
        FIELD_NAMES.lastServiceDate,
        form.formState
    );

    const disablingValues = [null, undefined, GAS_APPLIANCE_VALUES.NONE];
    const disabledRow = isGasService
        && (
            gasApplianceCountValue == null
            || disablingValues.includes(gasApplianceCountValue)
        );

    return (
        <GridItem
            as={ServiceGrid}
            area="remaining"
        >
            <GridItem area="services" width="full">
                <Box position="relative">
                    <ServiceIconButton
                        key={service.shortName}
                        label={service.name}
                        value={service.shortName}
                        color="primary.500"
                        cursor="default"
                        gridRow={1}
                        gridColumn={1}
                    />
                </Box>
            </GridItem>
            <GridItem area="last-service-date" width="full">
                <DateInputWithPickerField
                    control={form.control}
                    name={FIELD_NAMES.lastServiceDate}
                    isDisabled={disabledRow}
                />
            </GridItem>
            <GridItem area="next-service-date" width="full">
                <Box position="relative">
                    <DateInputWithPickerField
                        control={form.control}
                        name={FIELD_NAMES.nextServiceDate}
                        isDisabled={disabledRow}
                    />
                    {suggested && !lastDateError && frequencyMonths > 0 && (
                        <SuggestedDatePopover
                            suggested={suggested}
                            frequencyMonths={frequencyMonths}
                        />
                    )}
                </Box>
            </GridItem>
            <GridItem area="service-provider" width="full">
                <ServiceProviderSelect
                    control={form.control}
                    name={FIELD_NAMES.providedBy}
                    options={providerOptions}
                    isLoading={isLoadingProviderOptions}
                    isDisabled={disabledRow}
                />
            </GridItem>
            <GridItem area="further-works" width="full">
                <Controller
                    control={form.control}
                    name={FIELD_NAMES.isFurtherWorksRequired}
                    render={({ field }) => (
                        <BasicSelect
                            // eslint-disable-next-line no-nested-ternary
                            value={field.value === 1 ? '1' : (field.value === 0 ? '0' : null)}
                            options={[
                                {
                                    label: 'Yes',
                                    value: '1',
                                },
                                {
                                    label: 'No',
                                    value: '0',
                                },
                            ]}
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }

                                field.onChange(val.value === '1' ? 1 : 0);
                            }}
                            placeholder="Is rectification required?"
                            isDisabled={disabledRow}
                            isClearable={false}
                        />
                    )}
                />
            </GridItem>
            <GridItem area="action" width="40px" height="40px" alignContent="center">
                <FileInputWithCounter portfolioPropertyId={portfolioPropertyId} />
            </GridItem>
        </GridItem>
    );
}
